import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Collapse, Flex, Modal, Typography } from "antd";
import { CloseOutlined, CopyOutlined, ReloadOutlined } from "@ant-design/icons";
import api from "../../../api";
import notify from "../../../components/Notification";

const { Paragraph, Text, Title } = Typography;

function policyLabel(entry) {
    if (entry == null) return "";
    if (typeof entry === "string") return entry;
    if (typeof entry === "object") {
        return (
            entry.policy_name ||
            entry.name ||
            entry.PolicyName ||
            entry.arn ||
            entry.PolicyArn ||
            entry.policy_arn ||
            ""
        );
    }
    return String(entry);
}

const DRAWER_TITLE = "Required IAM policies for AWS integration";
const DRAWER_DESCRIPTION =
    "These are the IAM policies Atmosly expects for this cluster. Attach them in AWS to the same user or role you use for the connected cloud account, then use Re-check below. You do not need to reconnect or re-integrate the account — the same credentials pick up the new permissions automatically.";

/**
 * @param {{
 *   title?: string,
 *   policies_required?: unknown[],
 *   clusterLabel?: string,
 *   import_policy_name?: string | null,
 *   import_policy_document?: object | null,
 *   integrationUser?: string | null,
 *   clusterUniqueId?: string,
 *   onRechecked?: () => void,
 * }} data
 * @param {() => void} closeDrawer
 */
export default function AwsIntegrationPoliciesDrawer({ data, closeDrawer }) {
    const rawList = Array.isArray(data?.policies_required) ? data.policies_required : [];
    const policies = rawList.map((p, i) => ({ key: i, label: policyLabel(p) || `Policy ${i + 1}` })).filter((p) => p.label);

    // Custom import policy — render the exact name + JSON from the API. The
    // step is hidden entirely when either is missing (defensive null-handling):
    // a keys-based integration that already has the write actions, or an older
    // backend, simply won't return these fields.
    // Full access needs the superset (AtmoslyEKSFullAccessPolicy: import + S3/ECR for
    // pipelines); read-only setup needs the minimal import policy. Full-access intent =
    // an explicit upgrade OR a full_access cluster (e.g. the broader-access banner).
    const wantsFullAccessPolicy = !!data?.is_upgrade || data?.access_mode === "full_access";
    const chosenPolicyName = wantsFullAccessPolicy ? data?.full_access_policy_name : data?.import_policy_name;
    const chosenPolicyDocument = wantsFullAccessPolicy ? data?.full_access_policy_document : data?.import_policy_document;
    // Backend reports whether the exact required policy is already attached to the
    // integration (a live probe). If so, Step 1 is done — show it as complete and point
    // the user straight to "Re-check & re-import" instead of re-instructing the attach.
    const policyAttached = wantsFullAccessPolicy ? !!data?.full_access_policy_attached : !!data?.import_policy_attached;
    const importPolicyName =
        typeof chosenPolicyName === "string" && chosenPolicyName.trim()
            ? chosenPolicyName.trim()
            : null;
    const importPolicyDocument =
        chosenPolicyDocument && typeof chosenPolicyDocument === "object"
            ? chosenPolicyDocument
            : null;
    const showImportPolicyStep = Boolean(importPolicyName && importPolicyDocument);
    // Pretty-print with 2-space indent; the action list is whatever the
    // backend sends — nothing here is hardcoded.
    const formattedPolicyDocument = importPolicyDocument ? JSON.stringify(importPolicyDocument, null, 2) : "";
    const integrationUser =
        typeof data?.integrationUser === "string" && data.integrationUser.trim() ? data.integrationUser.trim() : null;

    const clusterUniqueId = data?.clusterUniqueId;
    // Read-only clusters re-import as read-only (view access entry, nothing in-cluster);
    // full-access clusters install the cluster-admin footprint. Drives the confirm modal.
    const isReadOnly = !!data?.is_read_only;
    // Upgrade-to-full mode: Option 2 installs the Ops Agent (full/admin token) instead of
    // the Debug Agent, and Option 1 calls upgrade-access instead of the recheck→reimport flow.
    const isUpgrade = !!data?.is_upgrade;
    // Ops Agent (full/admin token) whenever the target is full access — either an explicit
    // read-only->full upgrade, or a full_access import whose keys can't create the access entry.
    const useOpsAgent = isUpgrade || data?.access_mode === "full_access";
    const agentLabel = useOpsAgent ? "Agents" : "Debug Agent";
    // Token import (no cloud account): there's no AWS/GCP integration to attach IAM
    // policies to, so the ops agent is the only upgrade path. Hide every policy/IAM
    // section and show only the agent-install block.
    const agentOnly = !!data?.agent_only;
    const headerTitle = data?.title?.trim() ? data.title : (agentOnly ? "Upgrade to Full Access" : DRAWER_TITLE);
    const headerDescription = data?.description?.trim()
        ? data.description
        : (agentOnly
            ? "This cluster was imported via token, so there's no cloud account to attach IAM policies to. Install the Atmosly Ops Agent inside the cluster to grant full access."
            : DRAWER_DESCRIPTION);
    const [rechecking, setRechecking] = useState(false);
    const [recheckError, setRecheckError] = useState("");
    // After a successful re-check we confirm the full-access footprint before re-importing.
    const [showReimportConfirm, setShowReimportConfirm] = useState(false);
    const [reimporting, setReimporting] = useState(false);
    // Option 2: install an in-cluster agent (generated Helm command) instead of granting IAM writes.
    const [helmInfo, setHelmInfo] = useState(null);
    const [helmFetching, setHelmFetching] = useState(false);
    const [upgrading, setUpgrading] = useState(false);
    const navigate = useNavigate();

    const fetchAgentCommand = async () => {
        if (!clusterUniqueId || helmFetching) return;
        const endpoint = useOpsAgent ? "ops-agent-command" : "debug-agent-command";
        setHelmFetching(true);
        try {
            const res = await api.get(
                `${process.env.REACT_APP_API_URI}/api/cluster/cluster/${clusterUniqueId}/${endpoint}/`
            );
            const info = res?.data?.data || res?.data;
            if (info?.helm_command) setHelmInfo(info);
            else notify("error", `Could not generate the ${agentLabel} command.`);
        } catch (err) {
            notify("error", `Could not generate the ${agentLabel} install command.`);
        } finally {
            setHelmFetching(false);
        }
    };

    // Option 1 in upgrade mode: keys must now be write-capable (policy attached). Backend
    // validates and errors with guidance if not. Promotes the cluster to full_access.
    const handleUpgradeAccess = async () => {
        if (!clusterUniqueId || upgrading) return;
        setUpgrading(true);
        try {
            const res = await api.post(
                `${process.env.REACT_APP_API_URI}/api/cluster/cluster/${clusterUniqueId}/upgrade-access/`
            );
            if (res?.data?.status) {
                notify("success", "Upgrade to full access submitted", "This may take a few minutes.");
                if (typeof data?.onRechecked === "function") data.onRechecked();
                handleCancel();
                navigate("/clusters");
            } else {
                notify("error", "Could not upgrade access", res?.data?.data?.detail || "Attach the import policy, then try again.");
            }
        } catch (err) {
            notify("error", "Could not upgrade access", err?.response?.data?.data?.detail || "Attach the import policy, then try again.");
        } finally {
            setUpgrading(false);
        }
    };

    const handleCancel = () => {
        if (typeof closeDrawer === "function") closeDrawer();
    };

    const copyValue = (value, label) => {
        const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
        if (text == null || text === "") return;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard
                .writeText(text)
                .then(() => notify("success", `${label} copied to clipboard.`))
                .catch(() => notify("error", `Could not copy ${label.toLowerCase()}.`));
        } else {
            notify("error", `Could not copy ${label.toLowerCase()}.`);
        }
    };

    // Re-check: re-query cluster-details and read the live banner predicate.
    // If the integration is now write-capable the banner is gone, so refresh
    // the cluster shell (canonical path) and close. Otherwise surface the
    // exact-name / correct-user reminder so the user can fix and retry.
    const handleRecheck = async () => {
        if (!clusterUniqueId || rechecking) return;
        setRechecking(true);
        setRecheckError("");
        try {
            // recheck_aws_scope=1 forces a live IAM re-probe server-side so the
            // verdict reflects policies attached just now (the banner value is
            // otherwise a sticky cache refreshed only during an import).
            const res = await api.get(
                `${process.env.REACT_APP_API_URI}/api/cluster/cluster/${clusterUniqueId}/?recheck_aws_scope=1`
            );
            const stillNeedsUpgrade = res?.data?.data?.show_aws_integration_upgrade_banner;
            if (stillNeedsUpgrade) {
                setRecheckError(
                    `We still can't perform the required actions with these keys. Confirm the policy name is exactly ${importPolicyName} and that it's attached to the correct IAM user, then re-check. (IAM changes can take a few seconds to propagate.)`
                );
            } else {
                // Integration is now write-capable. Before re-importing, show the
                // full-access footprint so the user knows what will be installed,
                // then run the re-import only on explicit confirm.
                setShowReimportConfirm(true);
            }
        } catch (err) {
            setRecheckError("We couldn't re-check the integration right now. Please try again in a moment.");
        } finally {
            setRechecking(false);
        }
    };

    // Confirmed from the footprint dialog: run the write-heavy re-import. Same keys
    // are reused (no reconnect); runs in the background, so close + go to the list.
    const handleConfirmReimport = async () => {
        if (!clusterUniqueId || reimporting) return;
        setReimporting(true);
        try {
            await api.post(
                `${process.env.REACT_APP_API_URI}/api/cluster/cluster/${clusterUniqueId}/reimport/`
            );
            notify(
                "success",
                "AWS integration upgraded — re-import started",
                "Atmosly is re-running the remaining import steps with the new permissions. This can take a few minutes."
            );
            // Success: close the drawer and send the user to the cluster list, where the
            // re-import progress is visible.
            if (typeof data?.onRechecked === "function") data.onRechecked();
            handleCancel();
            navigate("/clusters");
        } catch (reimportErr) {
            notify(
                "warning",
                "Integration upgraded, but re-import didn't start",
                "The permissions are in place, but we couldn't start the re-import just now. Please try again from the cluster."
            );
        } finally {
            setReimporting(false);
            setShowReimportConfirm(false);
        }
    };

    const codeBlockStyle = {
        background: "#f5f5f5",
        border: "1px solid #d9d9d9",
        borderRadius: "6px",
        padding: "14px 16px",
        margin: 0,
        fontSize: "12px",
        lineHeight: 1.6,
        fontFamily: "Monaco, Courier, monospace",
        whiteSpace: "pre",
        overflow: "auto",
        maxHeight: "320px",
    };

    // The in-cluster agent install UI (generate + show the Helm command). Reused by the
    // "Option 2" section and by the agent-only (token-import) body.
    const helmCommandBlock = !helmInfo ? (
        <Button
            icon={<ReloadOutlined />}
            loading={helmFetching}
            disabled={!clusterUniqueId}
            onClick={fetchAgentCommand}
        >
            Generate Helm command
        </Button>
    ) : (
        <>
            {Array.isArray(helmInfo.creates) && helmInfo.creates.length > 0 && (
                <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
                    <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                        {helmInfo.creates_summary || `This installs the following resources in your cluster:`}
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {helmInfo.creates.map((c, i) => (
                            <li key={i} style={{ fontSize: 13, color: "#292929", marginBottom: 4 }}>
                                <Text code>{c.kind}</Text> <Text strong>{c.name}</Text>
                                {c.detail ? ` — ${c.detail}` : ""}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <Text strong style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Helm install command
            </Text>
            <Flex align="flex-start" gap={8} style={{ marginBottom: "8px" }}>
                <pre style={{ ...codeBlockStyle, flex: 1, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {helmInfo.helm_command}
                </pre>
                <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyValue(helmInfo.helm_command, "Helm command")}
                >
                    Copy
                </Button>
            </Flex>
            <Paragraph type="secondary" style={{ fontSize: "12px", marginBottom: 0 }}>
                Run inside the target cluster with an admin kubeconfig.{" "}
                {useOpsAgent ? "Full access is granted" : "Read access appears"} automatically
                once the agent registers.
            </Paragraph>
        </>
    );

    return (
        <>
            <Flex
                align="flex-start"
                gap={12}
                style={{
                    position: "absolute",
                    zIndex: 10,
                    top: 0,
                    left: 0,
                    right: 0,
                    width: "100%",
                    backgroundColor: "#132f44",
                    borderBottom: "1px solid #e9e9e9",
                    padding: "18px 20px 20px",
                    minHeight: 96,
                }}
            >
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "#fff", fontSize: 18 }} />}
                    onClick={handleCancel}
                    aria-label="Close drawer"
                    style={{ color: "#fff", flexShrink: 0, marginTop: 2 }}
                />
                <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                    <Title level={4} style={{ color: "#fff", margin: 0, marginBottom: 8, fontWeight: 600 }}>
                        {headerTitle}
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.88)", fontSize: 14, lineHeight: 1.5, display: "block" }}>
                        {headerDescription}
                    </Text>
                </div>
            </Flex>

            <Flex vertical style={{ marginTop: "184px", padding: "20px 24px 32px", maxWidth: "100%" }}>
                {agentOnly && (
                    <>
                        <Title level={5} style={{ margin: "0 0 6px", fontWeight: 600 }}>
                            Install the {useOpsAgent ? "Ops Agent" : "Debug Agent"}
                        </Title>
                        <Paragraph style={{ marginBottom: "16px", fontSize: "14px", color: "#292929" }}>
                            This cluster was imported via token, so there's no cloud account to attach IAM policies to.
                            Install the Atmosly {useOpsAgent ? "Ops Agent" : "Debug Agent"} inside this cluster with an admin
                            kubeconfig — it registers back to Atmosly and grants{useOpsAgent ? " full access" : " read access"} from
                            within the cluster. Nothing is created in your AWS account.
                        </Paragraph>
                        {useOpsAgent && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: "16px" }}
                                message="Nodegroup and AWS-managed addon installation won't be supported with this option — the Ops Agent grants access from inside the cluster only, not on your AWS account."
                            />
                        )}
                        {helmCommandBlock}
                    </>
                )}
                {!agentOnly && (
                <>
                <Paragraph style={{ marginBottom: "16px", fontSize: "14px", color: "#292929" }}>
                    Follow these steps in the <Text strong>AWS account</Text> linked to this cluster
                    {data?.clusterLabel ? (
                        <>
                            {" "}
                            (<Text code>{data.clusterLabel}</Text>)
                        </>
                    ) : null}
                    . Attach the policies to the same <Text strong>IAM user or role</Text> whose keys or role ARN you used when connecting this cloud account in Atmosly.
                </Paragraph>

                {/* Legacy fallback: an older backend that didn't return the custom
                    import policy. Surface whatever policy names came back so the user
                    still has something actionable. With a current backend the banner is
                    a single custom-policy step (the block below), so this is hidden. */}
                {!showImportPolicyStep && (
                    <>
                        <Title level={5} style={{ margin: "0 0 12px", fontWeight: 600 }}>
                            Attach the required policies to the integration identity
                        </Title>

                        <ol style={{ paddingLeft: "18px", margin: "0 0 20px", fontSize: "14px", color: "#292929", lineHeight: 1.6 }}>
                            <li style={{ marginBottom: "10px" }}>
                                Sign in to the <Text strong>AWS Management Console</Text> for that account and open <Text strong>IAM</Text>.
                            </li>
                            <li style={{ marginBottom: "10px" }}>
                                Open <Text strong>Users</Text> or <Text strong>Roles</Text>, depending on how you integrated (long-lived keys → usually a user; OIDC / assumed role → select the matching role).
                            </li>
                            <li style={{ marginBottom: "10px" }}>
                                Select the principal used for this Atmosly integration, then choose <Text strong>Add permissions</Text> →{" "}
                                <Text strong>Attach policies directly</Text> (or create an inline/custom policy if your security team requires it).
                            </li>
                            <li style={{ marginBottom: "10px" }}>
                                Attach <Text strong>each</Text> policy listed below. For AWS-managed policies, use the search box in the attach flow; for customer-managed policies, attach by name or ARN as provided by your administrator.
                            </li>
                            <li style={{ marginBottom: "10px" }}>
                                That's all that's needed in AWS. You do <Text strong>not</Text> need to reconnect or re-integrate the account in Atmosly — the same keys pick up the new permissions automatically.
                            </li>
                        </ol>

                        <Text strong style={{ display: "block", marginBottom: "10px", fontSize: "14px" }}>
                            Required policies for this cluster
                        </Text>
                        {policies.length > 0 ? (
                            <Flex vertical gap={8} style={{ marginBottom: "8px" }}>
                                {policies.map((p) => (
                                    <div
                                        key={p.key}
                                        style={{
                                            padding: "10px 12px",
                                            backgroundColor: "#f5f5f5",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "6px",
                                            fontFamily: "monospace",
                                            fontSize: "13px",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {p.label}
                                    </div>
                                ))}
                            </Flex>
                        ) : (
                            <Paragraph type="secondary" style={{ fontSize: "13px", marginBottom: 0 }}>
                                No policy names were returned in the cluster response. Use administrator-scoped credentials when reconnecting the cloud account, or ask your platform team which IAM policies Atmosly requires for this integration.
                            </Paragraph>
                        )}
                    </>
                )}

                {showImportPolicyStep && (
                    <>
                        <Title level={5} style={{ margin: "0 0 6px", fontWeight: 600 }}>
                            Option 1 — Create &amp; attach the custom import policy
                        </Title>
                        {policyAttached ? (
                            <Alert
                                type="success"
                                showIcon
                                style={{ marginBottom: "16px" }}
                                message={<span style={{ fontWeight: 600 }}>{importPolicyName} is already attached</span>}
                                description={'Step 1 is done — this policy is attached to your integration user. Go ahead and click "Re-check & re-import" below to finish.'}
                            />
                        ) : (
                            <>
                                <Paragraph style={{ marginBottom: "16px", fontSize: "14px", color: "#292929" }}>
                                    A keys-based integration can only run a read-only import until its IAM user can perform the EKS control-plane writes the import needs. No AWS-managed policy grants these, so create a custom policy with the exact JSON and name below, then attach it to the integration user.
                                </Paragraph>

                                <ol style={{ paddingLeft: "18px", margin: "0 0 20px", fontSize: "14px", color: "#292929", lineHeight: 1.6 }}>
                                    <li style={{ marginBottom: "10px" }}>
                                        In the AWS Console, open <Text strong>IAM</Text> → <Text strong>Policies</Text> →{" "}
                                        <Text strong>Create policy</Text> → <Text strong>JSON</Text> tab.
                                    </li>
                                    <li style={{ marginBottom: "10px" }}>
                                        Replace the editor contents with the JSON shown below (use <Text strong>Copy JSON</Text>).
                                    </li>
                                    <li style={{ marginBottom: "10px" }}>
                                        On the next screen, name the policy exactly{" "}
                                        <Text code>{importPolicyName}</Text>.{" "}
                                        <Text strong>The name must match exactly</Text> — detection is by policy name.
                                    </li>
                                    <li style={{ marginBottom: "10px" }}>
                                        Create the policy, then go to <Text strong>IAM</Text> → <Text strong>Users</Text> →{" "}
                                        <Text strong>{integrationUser ? integrationUser : "the integration user (whose access keys back this integration)"}</Text>{" "}
                                        → <Text strong>Add permissions</Text> → <Text strong>Attach policies directly</Text>, select{" "}
                                        <Text code>{importPolicyName}</Text>, and attach it.
                                    </li>
                                </ol>
                            </>
                        )}

                        <Text strong style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                            Policy name
                        </Text>
                        <Flex align="center" gap={8} style={{ marginBottom: "6px" }}>
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    padding: "10px 12px",
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "6px",
                                    fontFamily: "monospace",
                                    fontSize: "13px",
                                    wordBreak: "break-all",
                                }}
                            >
                                {importPolicyName}
                            </div>
                            <Button
                                icon={<CopyOutlined />}
                                onClick={() => copyValue(importPolicyName, "Policy name")}
                            >
                                Copy
                            </Button>
                        </Flex>
                        <Paragraph type="secondary" style={{ fontSize: "12px", marginBottom: "16px" }}>
                            The name must match exactly.
                        </Paragraph>

                        <Text strong style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                            Policy JSON
                        </Text>
                        <Collapse
                            style={{ marginBottom: "20px" }}
                            items={[
                                {
                                    key: "policy-json",
                                    label: <Text strong style={{ fontSize: "13px" }}>{importPolicyName} — policy document</Text>,
                                    extra: (
                                        <Button
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyValue(formattedPolicyDocument, "Policy JSON");
                                            }}
                                        >
                                            Copy JSON
                                        </Button>
                                    ),
                                    children: <pre style={codeBlockStyle}>{formattedPolicyDocument}</pre>,
                                },
                            ]}
                        />

                        <div style={{ height: 1, background: "#eaeaea", margin: "4px 0 20px" }} />

                        <Title level={5} style={{ margin: "0 0 12px", fontWeight: 600 }}>
                            Step 2 — Verify
                        </Title>
                        {recheckError ? (
                            <Alert
                                type="error"
                                showIcon
                                message={recheckError}
                                style={{ marginBottom: "12px" }}
                            />
                        ) : null}
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            loading={isUpgrade ? upgrading : rechecking}
                            disabled={!clusterUniqueId}
                            onClick={isUpgrade ? handleUpgradeAccess : handleRecheck}
                            style={{ background: "#132f44", borderColor: "#132f44" }}
                        >
                            {isUpgrade
                                ? "I've attached the policy — Upgrade to full access"
                                : "I've attached the policy — Re-check & re-import"}
                        </Button>

                        <div style={{ height: 1, background: "#eaeaea", margin: "24px 0 20px" }} />

                        <Title level={5} style={{ margin: "0 0 6px", fontWeight: 600 }}>
                            Option 2 — Install the {agentLabel} instead (no IAM changes)
                        </Title>
                        <Paragraph style={{ marginBottom: "16px", fontSize: "14px", color: "#292929" }}>
                            Prefer not to change IAM permissions? Install the Atmosly {agentLabel} inside this cluster. Run the
                            Helm command below with an admin kubeconfig — the agent registers back to Atmosly and grants
                            {useOpsAgent ? " full access" : " read access"} from within the cluster. Nothing is created in your AWS account.
                        </Paragraph>

                        {useOpsAgent && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: "16px" }}
                                message="Nodegroup and AWS-managed addon installation won't be supported with this option — the Ops Agent grants access from inside the cluster only, not on your AWS account. Choose Option 1 (attach the policy) if you need those."
                            />
                        )}

                        {helmCommandBlock}
                    </>
                )}
                </>
                )}
            </Flex>

            <Modal
                open={showReimportConfirm}
                title={isReadOnly ? "Enable read-only access — what Atmosly will set up" : "Upgrade to full access — what Atmosly will install"}
                okText={isReadOnly ? "Confirm & re-import (read-only)" : "Confirm & re-import"}
                cancelText="Cancel"
                confirmLoading={reimporting}
                onOk={handleConfirmReimport}
                onCancel={() => setShowReimportConfirm(false)}
                width={560}
            >
                {isReadOnly ? (
                    <>
                        <Paragraph style={{ marginBottom: 12 }}>
                            Your integration can now create the EKS access entry. Re-importing keeps this cluster
                            <Text strong> read-only</Text> — Atmosly will only be able to view resources:
                        </Paragraph>

                        <Text strong style={{ display: "block", marginBottom: 6 }}>In your cluster</Text>
                        <ul style={{ margin: "0 0 14px", paddingLeft: 18 }}>
                            <li style={{ marginBottom: 4 }}>Nothing is created — no namespace, ServiceAccount or workloads. Atmosly reads using your integration credentials.</li>
                        </ul>

                        <Text strong style={{ display: "block", marginBottom: 6 }}>In your AWS account</Text>
                        <ul style={{ margin: "0 0 14px", paddingLeft: 18 }}>
                            <li style={{ marginBottom: 4 }}>EKS access entry associated with <Text code>AmazonEKSAdminViewPolicy</Text> (read-only)</li>
                        </ul>

                        <Alert
                            type="info"
                            showIcon
                            message="Read-only lets Atmosly view nodes, pods and workloads; it cannot make changes. The re-import runs in the background and takes a few minutes."
                        />
                    </>
                ) : (
                    <>
                        <Paragraph style={{ marginBottom: 12 }}>
                            Your integration can now perform the required writes. Re-importing switches this cluster to
                            <Text strong> full access</Text> and installs the following:
                        </Paragraph>

                        <Text strong style={{ display: "block", marginBottom: 6 }}>In your cluster</Text>
                        <ul style={{ margin: "0 0 14px", paddingLeft: 18 }}>
                            <li style={{ marginBottom: 4 }}>Namespace <Text code>atmosly-addons</Text> (if not already present)</li>
                            <li style={{ marginBottom: 4 }}>ServiceAccount <Text code>atmosly-service-account</Text> with a long-lived token</li>
                            <li style={{ marginBottom: 4 }}>ClusterRoleBinding to <Text code>cluster-admin</Text> (full read/write)</li>
                            <li style={{ marginBottom: 4 }}>ArgoCD <Text code>Application</Text> (app-status) for health monitoring</li>
                        </ul>

                        <Text strong style={{ display: "block", marginBottom: 6 }}>In your AWS account</Text>
                        <ul style={{ margin: "0 0 14px", paddingLeft: 18 }}>
                            <li style={{ marginBottom: 4 }}>EKS access entry associated with <Text code>AmazonEKSClusterAdminPolicy</Text></li>
                        </ul>

                        <Alert
                            type="info"
                            showIcon
                            message="This grants Atmosly cluster-admin so it can install addons and perform actions/remediation. The re-import runs in the background and takes a few minutes."
                        />
                    </>
                )}
            </Modal>
        </>
    );
}
